import { type CSSProperties, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { CashFlowPoint } from "../overviewTypes";

/**
 * Money in (teal) vs money out (amber) monthly bar chart.
 * Duration + Compare year chips in the header.
 *
 * Responsive:
 *   desktop/tablet: full chart, both chips
 *   phone: bars shrink to 10px wide, Compare year chip hides, chip row wraps below title
 */

interface CashFlowChartProps {
  data: CashFlowPoint[];
}

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#ECEFF3",
  teal: "#0F6E56",
  amber: "#B45309",
};

const DURATIONS = ["Last 3 months", "Last 6 months", "This year", "All time"];

export function CashFlowChart({ data }: CashFlowChartProps) {
  const [duration, setDuration] = useState("Last 6 months");
  const [compare, setCompare] = useState(false);

  if (data.length === 0) return null;

  const maxValue = Math.max(...data.flatMap((p) => [p.moneyIn, p.moneyOut])) || 1;

  return (
    <section className="novala-cash-flow" style={sectionStyle}>
      <div className="novala-cf-head" style={headStyle}>
        <div>
          <h3 style={h3Style}>Cash flow</h3>
          <p style={subStyle}>Money in and money out, by month</p>
        </div>
        <div className="novala-cf-chips" style={chipsStyle}>
          <DurationChip value={duration} onChange={setDuration} />
          <button
            type="button"
            className="novala-cf-compare-chip"
            style={{ ...chipStyle, background: compare ? PALETTE.lineSoft : "transparent" }}
            onClick={() => setCompare((v) => !v)}
          >
            {compare ? "Comparing year" : "Compare year"}
          </button>
        </div>
      </div>

      <div style={chartStyle}>
        {data.map((point) => {
          const inH = Math.round((point.moneyIn / maxValue) * 130);
          const outH = Math.round((point.moneyOut / maxValue) * 130);
          return (
            <div key={point.month} style={colStyle}>
              <div style={pairStyle}>
                <div className="novala-cf-bar" style={{ ...barStyle, height: inH, background: PALETTE.teal }} />
                <div className="novala-cf-bar" style={{ ...barStyle, height: outH, background: PALETTE.amber }} />
              </div>
              <span style={monthLabelStyle}>{point.month}</span>
            </div>
          );
        })}
      </div>

      <div style={legendStyle}>
        <span style={legendItemStyle}>
          <span style={{ ...swatchStyle, background: PALETTE.teal }} /> Money in
        </span>
        <span style={legendItemStyle}>
          <span style={{ ...swatchStyle, background: PALETTE.amber }} /> Money out
        </span>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .novala-cf-head { flex-wrap: wrap !important; gap: 8px !important; }
          .novala-cf-chips { width: 100%; }
          .novala-cf-compare-chip { display: none !important; }
          .novala-cf-bar { width: 10px !important; }
        }
      `}</style>
    </section>
  );
}

interface DurationChipProps {
  value: string;
  onChange: (v: string) => void;
}

function DurationChip({ value, onChange }: DurationChipProps) {
  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...chipStyle,
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          paddingRight: 28,
          cursor: "pointer",
        }}
      >
        {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <ChevronDown
        size={10}
        strokeWidth={3}
        color={PALETTE.ink3}
        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
      />
    </div>
  );
}

const sectionStyle: CSSProperties = { marginBottom: 36 };

const headStyle: CSSProperties = {
  display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14,
};

const h3Style: CSSProperties = {
  fontSize: 15, fontWeight: 700, color: PALETTE.ink, margin: "0 0 2px",
};

const subStyle: CSSProperties = {
  fontSize: 12, color: PALETTE.ink3, margin: 0,
};

const chipsStyle: CSSProperties = {
  display: "inline-flex", gap: 8, alignItems: "center",
};

const chipStyle: CSSProperties = {
  background: "transparent",
  border: `1px solid ${PALETTE.line}`,
  color: "#263238",
  padding: "5px 12px",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "inherit",
  outline: "none",
};

const chartStyle: CSSProperties = {
  height: 160, display: "flex", alignItems: "flex-end", gap: 10, padding: "0 4px",
};

const colStyle: CSSProperties = {
  flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
};

const pairStyle: CSSProperties = {
  display: "flex", alignItems: "flex-end", gap: 2, height: 130,
};

const barStyle: CSSProperties = {
  width: 14, borderRadius: "2px 2px 0 0",
};

const monthLabelStyle: CSSProperties = {
  fontSize: 10.5, color: PALETTE.ink4,
};

const legendStyle: CSSProperties = {
  display: "inline-flex", gap: 18, marginTop: 10, fontSize: 11.5, color: PALETTE.ink3,
};

const legendItemStyle: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6,
};

const swatchStyle: CSSProperties = {
  width: 10, height: 10, borderRadius: 2,
};